import { ArrowLeft } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ASSETS = {
  logo: "/logo.png",
};

export default function Accessibility() {
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <h1 className="heading-xl text-3xl md:text-4xl text-white mb-8">Accessibility Statement</h1>
        <p className="text-white/40 text-sm mb-12">Last updated: February 2026</p>

        <div className="space-y-12 body-text text-white/70">
          {/* Commitment */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Our Commitment to Accessibility</h2>
            <p>
              Gaffer Athletics is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal access to all users.
            </p>
          </section>

          {/* Standards */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Conformance Status</h2>
            <p className="mb-4">
              We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. These guidelines explain how to make web content more accessible for people with disabilities and more user-friendly for everyone.
            </p>
            <p>
              This website is designed to comply with the European Accessibility Act (Directive (EU) 2019/882), which comes into full effect on June 28, 2025.
            </p>
          </section>

          {/* Measures Taken */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Accessibility Measures</h2>
            <p className="mb-4">Gaffer Athletics takes the following measures to ensure accessibility:</p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>Include accessibility as part of our design and development process</li>
              <li>Provide clear and consistent navigation</li>
              <li>Ensure sufficient color contrast for text readability</li>
              <li>Use semantic HTML for proper document structure</li>
              <li>Provide text alternatives for non-text content</li>
              <li>Ensure all interactive elements are keyboard accessible</li>
              <li>Design forms with clear labels and error messages</li>
              <li>Test with assistive technologies</li>
            </ul>
          </section>

          {/* Technical Specifications */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Technical Specifications</h2>
            <p className="mb-4">
              Accessibility of this website relies on the following technologies:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>HTML5</li>
              <li>WAI-ARIA</li>
              <li>CSS</li>
              <li>JavaScript</li>
            </ul>
            <p className="mt-4">
              These technologies are relied upon for conformance with the accessibility standards used.
            </p>
          </section>

          {/* Known Limitations */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Known Limitations</h2>
            <p className="mb-4">
              Despite our best efforts to ensure accessibility, there may be some limitations. Below is a description of known issues:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li><strong className="text-white">Video content:</strong> Our hero video does not currently have audio descriptions. We provide text alternatives describing the content.</li>
              <li><strong className="text-white">PDF documents:</strong> Some older PDF documents may not be fully accessible. We are working to remediate these.</li>
            </ul>
            <p className="mt-4">
              We are actively working to address these issues and improve accessibility.
            </p>
          </section>

          {/* Assistive Technologies */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Compatibility with Assistive Technologies</h2>
            <p className="mb-4">
              This website is designed to be compatible with the following assistive technologies:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>Screen readers (NVDA, JAWS, VoiceOver)</li>
              <li>Screen magnification software</li>
              <li>Speech recognition software</li>
              <li>Keyboard-only navigation</li>
            </ul>
          </section>

          {/* Assessment Methods */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Assessment Methods</h2>
            <p className="mb-4">
              Gaffer Athletics assessed the accessibility of this website using the following methods:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>Self-evaluation using automated testing tools</li>
              <li>Manual testing with keyboard navigation</li>
              <li>Testing with screen readers</li>
              <li>Review against WCAG 2.1 Level AA criteria</li>
            </ul>
          </section>

          {/* Feedback */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Feedback and Contact</h2>
            <p className="mb-4">
              We welcome your feedback on the accessibility of this website. If you encounter accessibility barriers or have suggestions for improvement, please contact us:
            </p>
            <ul className="list-none space-y-2 text-white/60">
              <li><strong className="text-white">Email:</strong> contact@gafferathletics.com</li>
              <li><strong className="text-white">Address:</strong> 8870 Langå, Denmark</li>
            </ul>
            <p className="mt-4">
              We aim to respond to accessibility feedback within 5 business days.
            </p>
          </section>

          {/* Enforcement */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Enforcement Procedure</h2>
            <p>
              If you are not satisfied with our response to your accessibility concern, you may escalate the matter to the relevant national enforcement body. In Denmark, this is the Agency for Digital Government (Digitaliseringsstyrelsen).
            </p>
          </section>

          {/* Date */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Statement Approval</h2>
            <p>
              This accessibility statement was approved by Gaffer Athletics management on February 15, 2026.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/30 text-sm">
            © 2026 Gaffer Athletics. Engineering for the Sideline.
          </p>
        </div>
      </footer>
    </div>
  );
}
